import React from "react";
import { Modal, ModalHeader, ModalBody, Button, Table } from "reactstrap";

const ModalAyudaMetrica = ({ isOpen, toggle, metrica }) => {
  const contenidoMetricas = {
    total_cotizaciones: {
      titulo: "Total Facturado (USD)",
      icono: "ri-dollar-circle-line",
      descripcion: "Suma total de todas las facturas convertidas a dólares en el período seleccionado.",
      calculo: {
        formula: "Σ(montoDolar de todas las facturas)",
        ejemplo: "Si tienes 100 facturas de $50 cada una = $5,000 USD"
      },
      interpretacion: [
        { texto: "Mide el volumen total de facturación", tipo: "info" },
        { texto: "Permite comparar períodos (mes vs mes, año vs año)", tipo: "info" },
        { texto: "Tendencia al alza indica crecimiento del negocio", tipo: "success" },
        { texto: "Tendencia a la baja requiere análisis de causas", tipo: "warning" }
      ],
      recomendaciones: [
        "Compara con períodos anteriores para identificar tendencias",
        "Analiza junto con cantidad de facturas para entender el ticket promedio",
        "Revisa si hay estacionalidad en tu negocio"
      ]
    },
    facturas_registradas: {
      titulo: "Facturas Registradas",
      icono: "ri-receipt-line",
      descripcion: "Cantidad total de facturas procesadas en el período, divididas en agudas y crónicas.",
      calculo: {
        formula: "Total = Facturas Agudas + Facturas Crónicas",
        ejemplo: "368 agudas + 671 crónicas = 1,039 facturas totales"
      },
      interpretacion: [
        { texto: "Indica el volumen operativo del negocio", tipo: "info" },
        { texto: "Permite calcular el promedio por factura", tipo: "info" },
        { texto: "Aumento sostenido indica crecimiento", tipo: "success" },
        { texto: "Caída abrupta puede indicar problemas operativos", tipo: "danger" }
      ],
      recomendaciones: [
        "Monitorea la proporción agudas/crónicas (debe ser estable)",
        "Compara con capacidad operativa para detectar saturación",
        "Analiza días con picos para optimizar recursos"
      ]
    },
    facturas_agudas: {
      titulo: "Facturas Agudas",
      icono: "ri-file-line",
      descripcion: "Facturas de medicamentos para tratamientos puntuales o de corta duración.",
      calculo: {
        formula: "Cantidad de facturas con tipo = 'Aguda'",
        ejemplo: "368 facturas agudas con promedio de $49.69 USD"
      },
      interpretacion: [
        { texto: "Representan tratamientos de corta duración", tipo: "info" },
        { texto: "Suelen tener mayor variabilidad de precios", tipo: "warning" },
        { texto: "Percentil 50 (mediana) más confiable que el promedio", tipo: "info" },
        { texto: "Outliers pueden indicar casos complejos o errores", tipo: "warning" }
      ],
      estadisticas: [
        { nombre: "Promedio USD", descripcion: "Costo promedio por factura aguda" },
        { nombre: "Desviación Estándar", descripcion: "Variabilidad de los precios (±)" },
        { nombre: "Percentil 50 (P50)", descripcion: "Mediana - valor típico sin outliers" },
        { nombre: "Coeficiente de Variación", descripcion: "Consistencia de precios (%)" }
      ],
      recomendaciones: [
        "Revisa outliers para detectar errores de captura",
        "Monitorea el CV (Coeficiente de Variación) - ideal <50%",
        "Compara P50 vs Promedio para detectar valores extremos"
      ]
    },
    facturas_cronicas: {
      titulo: "Facturas Crónicas",
      icono: "ri-file-line",
      descripcion: "Facturas de medicamentos para tratamientos continuos o de larga duración.",
      calculo: {
        formula: "Cantidad de facturas con tipo = 'Crónica'",
        ejemplo: "671 facturas crónicas con promedio de $24.43 USD"
      },
      interpretacion: [
        { texto: "Representan tratamientos de larga duración", tipo: "info" },
        { texto: "Suelen tener precios más estables que agudas", tipo: "success" },
        { texto: "Requieren seguimiento de fechas de vigencia", tipo: "warning" },
        { texto: "Base de ingresos recurrentes del negocio", tipo: "info" }
      ],
      estadisticas: [
        { nombre: "Promedio USD", descripcion: "Costo promedio por factura crónica" },
        { nombre: "Desviación Estándar", descripcion: "Variabilidad de los precios (±)" },
        { nombre: "Percentil 50 (P50)", descripcion: "Mediana - valor típico sin outliers" },
        { nombre: "Coeficiente de Variación", descripcion: "Consistencia de precios (%)" }
      ],
      recomendaciones: [
        "Monitorea fechas de vigencia para renovaciones",
        "Identifica pacientes próximos a vencer (Próximo Mes)",
        "Mantén CV bajo para predecir ingresos recurrentes"
      ]
    },
    pareto: {
      titulo: "Principio de Pareto (80/20)",
      icono: "ri-pie-chart-alt-2-line",
      descripcion: "Porcentaje de proveedores que generan el 80% de la facturación total.",
      calculo: {
        formula: "% = (Proveedores que suman 80% del total / Total proveedores) × 100",
        ejemplo: "Si 5 de 20 proveedores generan 80% = 25%"
      },
      interpretacion: [
        { texto: "Ideal: ≤20% (distribución balanceada)", tipo: "success", color: "verde" },
        { texto: "Aceptable: 20-30% (concentración moderada)", tipo: "warning", color: "amarillo" },
        { texto: "Crítico: >30% (alta dependencia)", tipo: "danger", color: "rojo" }
      ],
      alertas: {
        verde: "Distribución saludable - riesgo bajo",
        amarillo: "Concentración moderada - monitorear",
        rojo: "Alta dependencia - diversificar urgente"
      },
      recomendaciones: [
        "Valor alto (>30%): Diversifica tu cartera de proveedores",
        "Valor bajo (<20%): Mantén relaciones con proveedores clave",
        "Monitorea cambios bruscos que indiquen pérdida de clientes"
      ]
    },
    delivery: {
      titulo: "Delivery",
      icono: "ri-cycling-line",
      descripcion: "Porcentaje de solicitudes entregadas exitosamente respecto al total.",
      calculo: {
        formula: "% = (Solicitudes Entregadas / Total Solicitudes) × 100",
        ejemplo: "387 entregadas de 1,039 solicitudes = 37.19%"
      },
      interpretacion: [
        { texto: "Excelente: ≥70% (alta eficiencia)", tipo: "success", color: "verde" },
        { texto: "Aceptable: 50-70% (mejorable)", tipo: "warning", color: "amarillo" },
        { texto: "Crítico: <50% (problemas operativos)", tipo: "danger", color: "rojo" }
      ],
      alertas: {
        verde: "Operación eficiente - mantener estándar",
        amarillo: "Revisar procesos de entrega",
        rojo: "Problemas críticos - acción inmediata"
      },
      factores: [
        "Disponibilidad de motorizados",
        "Distancia y zonas de cobertura",
        "Horarios de entrega",
        "Calidad de direcciones de clientes"
      ],
      recomendaciones: [
        "Valor bajo: Aumenta cobertura de motorizados",
        "Analiza solicitudes 'En Tránsito' o 'Por Reprogramar'",
        "Revisa calificaciones de motorizados",
        "Identifica zonas con baja tasa de entrega"
      ]
    },
    calificacion: {
      titulo: "Calificación Promedio",
      icono: "ri-star-line",
      descripcion: "Promedio de TODAS las calificaciones de solicitudes con facturas en el período seleccionado (escala 1-5 estrellas). Incluye múltiples calificaciones por solicitud crónica.",
      calculo: {
        formula: "Promedio = Σ(todas las calificaciones) / Total de calificaciones",
        ejemplo: "Si hay 450 calificaciones (incluyendo múltiples por crónicas) con promedio 4.69",
        nota: "⭐ IMPORTANTE: Se incluyen TODAS las calificaciones de las solicitudes filtradas, independientemente de cuándo fueron creadas. Esto proporciona el contexto completo del servicio."
      },
      interpretacion: [
        { texto: "Excelente: ≥4.5 estrellas", tipo: "success", color: "verde" },
        { texto: "Aceptable: 4.0-4.5 estrellas", tipo: "warning", color: "amarillo" },
        { texto: "Crítico: <4.0 estrellas", tipo: "danger", color: "rojo" }
      ],
      alertas: {
        verde: "Calidad excelente - mantener estándar",
        amarillo: "Revisar comentarios y mejorar",
        rojo: "Problemas graves - acción correctiva urgente"
      },
      metricas: [
        { nombre: "% Satisfacción", descripcion: "Porcentaje de calificaciones ≥4 estrellas" },
        { nombre: "Total Calificadas", descripcion: "Cantidad TOTAL de calificaciones (puede ser > solicitudes)" },
        { nombre: "Total Solicitudes", descripcion: "Cantidad de solicitudes en el período" }
      ],
      tiposSolicitud: [
        { tipo: "Agudas", descripcion: "1 entrega → máximo 1 calificación por solicitud" },
        { tipo: "Crónicas", descripcion: "Hasta 6 entregas → hasta 6 calificaciones por solicitud" }
      ],
      filtrado: {
        titulo: "Lógica de Filtrado",
        descripcion: "Las solicitudes se filtran por fecha de factura, pero las calificaciones NO se filtran por fecha de creación.",
        ejemplo: "Si una solicitud tiene factura en agosto pero fue calificada en septiembre, la calificación se incluye en el dashboard de agosto.",
        razon: "Esto proporciona el contexto completo del servicio y evita perder feedback valioso de calificaciones tardías."
      },
      recomendaciones: [
        "Calificación baja: Lee comentarios para identificar problemas",
        "Bajo % de calificaciones: Incentiva feedback de usuarios",
        "Monitorea tendencia - caída sostenida requiere acción",
        "Compara con delivery - ambos deben estar alineados",
        "⭐ NUEVO: Revisa calificaciones por entrega en solicitudes crónicas para detectar patrones"
      ]
    }
  };

  const contenido = contenidoMetricas[metrica];

  if (!contenido) return null;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <i className={`${contenido.icono} me-2 text-primary`}></i>
        {contenido.titulo}
      </ModalHeader>
      <ModalBody>
        {/* Descripción */}
        <div className="mb-4">
          <h5 className="text-primary mb-3">
            <i className="ri-information-line me-2"></i>
            ¿Qué es esta métrica?
          </h5>
          <p className="text-muted">{contenido.descripcion}</p>
        </div>

        {/* Cálculo */}
        {contenido.calculo && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-calculator-line me-2"></i>
              ¿Cómo se calcula?
            </h5>
            <div className="alert alert-info">
              <p className="mb-2">
                <strong>Fórmula:</strong>
                <br />
                <code className="text-dark">{contenido.calculo.formula}</code>
              </p>
              <p className={contenido.calculo.nota ? "mb-2" : "mb-0"}>
                <strong>Ejemplo:</strong>
                <br />
                {contenido.calculo.ejemplo}
              </p>
              {contenido.calculo.nota && (
                <div className="alert alert-warning mb-0 mt-2">
                  <small>{contenido.calculo.nota}</small>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Interpretación */}
        {contenido.interpretacion && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-eye-line me-2"></i>
              ¿Cómo interpretarla?
            </h5>
            <ul className="list-unstyled">
              {contenido.interpretacion.map((item, index) => (
                <li key={index} className="mb-2">
                  <i className={`ri-checkbox-circle-line text-${item.tipo} me-2`}></i>
                  {item.color && (
                    <span className={`badge bg-${item.tipo === 'success' ? 'success' : item.tipo === 'warning' ? 'warning' : 'danger'}-subtle text-${item.tipo === 'success' ? 'success' : item.tipo === 'warning' ? 'warning' : 'danger'} me-2`}>
                      {item.color === 'verde' ? '🟢' : item.color === 'amarillo' ? '🟡' : '🔴'}
                    </span>
                  )}
                  {item.texto}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Alertas */}
        {contenido.alertas && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-alert-line me-2"></i>
              Sistema de Alertas
            </h5>
            <div className="table-responsive">
              <Table className="table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '20%' }}>Color</th>
                    <th>Significado</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="badge bg-success">🟢 Verde</span></td>
                    <td>{contenido.alertas.verde}</td>
                  </tr>
                  <tr>
                    <td><span className="badge bg-warning">🟡 Amarillo</span></td>
                    <td>{contenido.alertas.amarillo}</td>
                  </tr>
                  <tr>
                    <td><span className="badge bg-danger">🔴 Rojo</span></td>
                    <td>{contenido.alertas.rojo}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* Estadísticas (para agudas/crónicas) */}
        {contenido.estadisticas && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-bar-chart-line me-2"></i>
              Estadísticas Incluidas
            </h5>
            <div className="table-responsive">
              <Table className="table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '35%' }}>Estadística</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {contenido.estadisticas.map((stat, index) => (
                    <tr key={index}>
                      <td><strong>{stat.nombre}</strong></td>
                      <td>{stat.descripcion}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* Métricas (para calificación) */}
        {contenido.metricas && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-list-check me-2"></i>
              Métricas Relacionadas
            </h5>
            <div className="table-responsive">
              <Table className="table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '35%' }}>Métrica</th>
                    <th>Descripción</th>
                  </tr>
                </thead>
                <tbody>
                  {contenido.metricas.map((metrica, index) => (
                    <tr key={index}>
                      <td><strong>{metrica.nombre}</strong></td>
                      <td>{metrica.descripcion}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}

        {/* Tipos de Solicitud (para calificación) */}
        {contenido.tiposSolicitud && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-file-list-line me-2"></i>
              Tipos de Solicitud
            </h5>
            <div className="alert alert-light border">
              {contenido.tiposSolicitud.map((tipo, index) => (
                <div key={index} className={index < contenido.tiposSolicitud.length - 1 ? "mb-2 pb-2 border-bottom" : ""}>
                  <div className="d-flex align-items-start">
                    <i className="ri-arrow-right-circle-fill text-primary me-2 mt-1"></i>
                    <div>
                      <strong className="text-dark">{tipo.tipo}:</strong>
                      <span className="text-muted ms-2">{tipo.descripcion}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lógica de Filtrado (para calificación) */}
        {contenido.filtrado && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-filter-line me-2"></i>
              {contenido.filtrado.titulo}
            </h5>
            <div className="alert alert-info">
              <p className="mb-2">
                <strong>Descripción:</strong>
                <br />
                {contenido.filtrado.descripcion}
              </p>
              <p className="mb-2">
                <strong>Ejemplo:</strong>
                <br />
                {contenido.filtrado.ejemplo}
              </p>
              <p className="mb-0">
                <strong>Razón:</strong>
                <br />
                {contenido.filtrado.razon}
              </p>
            </div>
          </div>
        )}

        {/* Factores (para delivery) */}
        {contenido.factores && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-list-check me-2"></i>
              Factores que Afectan esta Métrica
            </h5>
            <ul className="list-unstyled">
              {contenido.factores.map((factor, index) => (
                <li key={index} className="mb-2">
                  <i className="ri-arrow-right-s-line text-primary me-2"></i>
                  {factor}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Recomendaciones */}
        {contenido.recomendaciones && (
          <div className="mb-4">
            <h5 className="text-primary mb-3">
              <i className="ri-lightbulb-line me-2"></i>
              Recomendaciones
            </h5>
            <ul className="list-unstyled">
              {contenido.recomendaciones.map((rec, index) => (
                <li key={index} className="mb-2">
                  <i className="ri-check-line text-success me-2"></i>
                  {rec}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="text-center">
          <Button color="primary" onClick={toggle}>
            <i className="ri-check-line me-1"></i>
            Entendido
          </Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ModalAyudaMetrica;
